/**
 * CopyableId — click to copy an id/uuid to the clipboard, with a toast.
 * Used in the Flow Optimizer header for the flow id + campaign id so users
 * can grab the exact identifier of what they're editing.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC, useCallback } from 'react';
import { Copy } from 'lucide-react';

import { cn } from '@/lib/utils';
import { useToast } from '@/providers/ToastProvider';

export interface CopyableIdProps {
  value: string;
  /** Human label for the toast + aria (e.g. "Flow ID"). */
  label: string;
  className?: string;
}

const CopyableId: FC<CopyableIdProps> = ({ value, label, className }) => {
  const toast = useToast();

  const onCopy = useCallback(() => {
    if (!navigator.clipboard) {
      toast.error(`Could not copy ${label}`);
      return;
    }
    navigator.clipboard
      .writeText(value)
      .then(() => toast.success(`${label} copied`, value))
      .catch(() => toast.error(`Could not copy ${label}`));
  }, [value, label, toast]);

  return (
    <button
      type="button"
      onClick={onCopy}
      title={`Copy ${label}: ${value}`}
      aria-label={`Copy ${label}`}
      className={cn(
        'group inline-flex items-center gap-1 rounded font-mono text-[10px] tabular-nums leading-none text-muted-foreground transition-colors hover:text-foreground',
        className,
      )}
    >
      <span className="whitespace-nowrap">{value}</span>
      <Copy className="h-2.5 w-2.5 shrink-0 opacity-50 group-hover:opacity-100" aria-hidden />
    </button>
  );
};

export default CopyableId;
